import * as React from 'react';
import { BookOpen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Select } from '@/components/forms/Select';
import {
  buildSpecialtyOptionsForLevels,
  filterSchoolClassesByLevels,
  toIdLabelOptions,
} from '@/components/sessions/inperson/session-form-utils';
import { getSchoolClasses } from '@/services/establishments.service';
import { getSpecialties } from '@/services/specialties.service';
import type { Establishment } from '@/types/establishment.types';
import type { Level } from '@/types/level.types';
import type { Subject } from '@/types/subject.types';
import type {
  SessionFormData,
  SessionFormFieldChange,
  SessionFormFieldValidation,
} from '@/types/inperson/session.types';

/** Props for {@link TutoringSessionAcademicSection}. */
interface Props {
  form: SessionFormData;
  onFieldChange: SessionFormFieldChange;
  onBlur: (field: string) => void;
  validation: SessionFormFieldValidation;
  levels: Level[];
  subjects: Subject[];
  selectedEstablishment?: Establishment | null;
}

/** Programs, subjects, and academic fields for a tutoring session. */
export function TutoringSessionAcademicSection({
  form,
  onFieldChange,
  onBlur,
  validation,
  levels,
  subjects,
  selectedEstablishment,
}: Readonly<Props>) {
  const { data: schoolClassesRes } = useQuery({
    queryKey: ['school-classes-session', form.establishmentId],
    queryFn: () =>
      getSchoolClasses({
        establishmentId: form.establishmentId || undefined,
        pageSize: 10,
      }),
    enabled: !!form.establishmentId && form.levelIds.length > 0,
  });

  const { data: allSpecialties = [] } = useQuery({
    queryKey: ['specialties', 'active'],
    queryFn: () => getSpecialties(true),
  });

  const schoolClasses = filterSchoolClassesByLevels(schoolClassesRes?.data ?? [], form.levelIds);
  const specialtyOptions = buildSpecialtyOptionsForLevels(allSpecialties, form.levelIds);
  const isHighSchool = selectedEstablishment?.type === 'lycee';
  const levelMultiOptions = toIdLabelOptions(levels, (level) => level.label);
  const subjectMultiOptions = toIdLabelOptions(subjects, (subject) => subject.label);
  const classOptions = toIdLabelOptions(schoolClasses, (schoolClass) => schoolClass.name);

  function handleLevelChange(values: string[]): void {
    onFieldChange('levelIds', values);
  }
  function handleLevelBlur(): void {
    onBlur('levelIds');
  }
  function handleSubjectChange(values: string[]): void {
    onFieldChange('subjectIds', values);
  }
  function handleSubjectBlur(): void {
    onBlur('subjectIds');
  }
  function handleClassChange(value: string): void {
    onFieldChange('classId', value);
  }
  function handleClassBlur(): void {
    onBlur('classId');
  }
  function handleSpecialtyChange(values: string[]): void {
    onFieldChange('specialtyIds', values);
  }
  function handleSpecialtyBlur(): void {
    onBlur('specialtyIds');
  }
  function handleTopicChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('topic', e.target.value);
  }
  function handleTopicBlur(): void {
    onBlur('topic');
  }
  function handleNotesChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('internalNotes', e.target.value);
  }
  function handleNotesBlur(): void {
    onBlur('internalNotes');
  }

  function renderClassField(): React.ReactNode {
    if (!form.establishmentId || form.levelIds.length === 0) return null;
    return (
      <Select
        label="Classe (optionnel)"
        placeholder="Toutes les classes"
        options={classOptions}
        value={form.classId}
        onChange={handleClassChange}
        onBlur={handleClassBlur}
        size="sm"
      />
    );
  }

  function renderSpecialtyField(): React.ReactNode {
    if (!isHighSchool || form.levelIds.length === 0 || specialtyOptions.length === 0) {
      return null;
    }
    return (
      <MultiSelect
        label="Spécialités (optionnel)"
        placeholder="Sélectionner des spécialités"
        options={specialtyOptions}
        value={form.specialtyIds}
        onChange={handleSpecialtyChange}
        onBlur={handleSpecialtyBlur}
        maxVisibleTags={3}
        inputSize="sm"
      />
    );
  }

  return (
    <>
      <div className="form-section">
        <h4 className="form-section-title">
          <BookOpen className="w-4 h-4" />
          Détails académiques
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <MultiSelect
            label="Niveaux"
            placeholder="Sélectionner un ou plusieurs niveaux"
            options={levelMultiOptions}
            value={form.levelIds}
            onChange={handleLevelChange}
            onBlur={handleLevelBlur}
            state={validation.getFieldState('levelIds')}
            error={validation.getFieldError('levelIds')}
            maxVisibleTags={3}
            inputSize="sm"
          />
          <MultiSelect
            label="Matières"
            placeholder="Sélectionner une ou plusieurs matières"
            options={subjectMultiOptions}
            value={form.subjectIds}
            onChange={handleSubjectChange}
            onBlur={handleSubjectBlur}
            state={validation.getFieldState('subjectIds')}
            error={validation.getFieldError('subjectIds')}
            required
            maxVisibleTags={5}
            inputSize="sm"
          />
          {renderClassField()}
          {renderSpecialtyField()}
          <Input
            label="Sujet"
            placeholder="Ex: Révision mathématiques"
            value={form.topic}
            onChange={handleTopicChange}
            onBlur={handleTopicBlur}
            state={validation.getFieldState('topic')}
            error={validation.getFieldError('topic')}
            helperText="Sujet ou thème principal de la séance"
            maxLength={255}
            inputSize="sm"
          />
        </div>
      </div>
      <div className="form-section">
        <Input
          label="Notes internes"
          placeholder="Ex: Élève en difficulté, prévoir support"
          value={form.internalNotes}
          onChange={handleNotesChange}
          onBlur={handleNotesBlur}
          state={validation.getFieldState('internalNotes')}
          error={validation.getFieldError('internalNotes')}
          helperText="Notes privées visibles par les administrateurs"
          maxLength={1000}
          inputSize="sm"
        />
      </div>
    </>
  );
}
